import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, TrendingUp, Shield, Globe, Sun, Wind, Battery } from 'lucide-react';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';

export default function EnhancedHero() {
    const { user } = useFirebaseAuth();

    return (
        <div className="relative min-h-screen overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a2332 0%, #0f1a2a 50%, #0a1120 100%)' }}>
            {/* Animated Background with Grid Pattern */}
            <div className="absolute inset-0 opacity-20">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `
              linear-gradient(rgba(110, 255, 157, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(110, 255, 157, 0.1) 1px, transparent 1px)
            `,
                        backgroundSize: '40px 40px'
                    }}
                />
            </div>

            {/* Floating Particles */}
            {[...Array(20)].map((_, i) => (
                <motion.div
                    key={i}
                    className="absolute w-1 h-1 rounded-full"
                    style={{
                        background: i % 3 === 0 ? 'var(--neon-green)' : i % 3 === 1 ? 'var(--cyber-blue)' : 'var(--electric-purple)',
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        boxShadow: `0 0 10px ${i % 3 === 0 ? 'var(--neon-green)' : i % 3 === 1 ? 'var(--cyber-blue)' : 'var(--electric-purple)'}`
                    }}
                    animate={{
                        y: [0, -30, 0],
                        opacity: [0.3, 1, 0.3],
                        scale: [1, 1.5, 1]
                    }}
                    transition={{
                        duration: 3 + Math.random() * 2,
                        repeat: Infinity,
                        delay: Math.random() * 2
                    }}
                />
            ))}

            {/* Main Content Container */}
            <div className="relative z-10 container mx-auto px-4 py-20">
                {/* Header Section */}
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <motion.div
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full mb-8"
                        style={{
                            background: 'rgba(110, 255, 157, 0.1)',
                            border: '1px solid rgba(110, 255, 157, 0.3)',
                            backdropFilter: 'blur(10px)'
                        }}
                        whileHover={{ scale: 1.05 }}
                    >
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--neon-green)' }}></div>
                        <span className="text-sm font-mono font-bold" style={{ color: 'var(--neon-green)' }}>
                            BLOCKCHAIN-POWERED RENEWABLE ENERGY
                        </span>
                    </motion.div>

                    <motion.h1
                        className="text-4xl md:text-6xl lg:text-7xl font-bold font-mono mb-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                    >
                        <span className="block mb-2" style={{ color: 'var(--primary-text)' }}>Invest in</span>
                        <span className="block mb-2" style={{
                            background: 'linear-gradient(135deg, var(--neon-green), var(--cyber-blue))',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '0 0 30px rgba(110, 255, 157, 0.5)'
                        }}>
                            Green Energy Today
                        </span>
                    </motion.h1>

                    <motion.p
                        className="text-lg md:text-xl max-w-3xl mx-auto mb-8 font-light"
                        style={{ color: 'var(--secondary-text)' }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.8 }}
                    >
                        A blockchain-based renewable energy projects platform that combines cutting-edge
                        blockchain technology with sustainable energy investments.
                    </motion.p>
                </motion.div>

                {/* 3D Visualization Container - Similar to reference images */}
                <motion.div
                    className="relative max-w-7xl mx-auto mb-16"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6, duration: 1 }}
                >
                    {/* Main 3D Scene Container */}
                    <div className="relative" style={{ perspective: '1000px' }}>
                        {/* Central Holographic Cylinder/Platform */}
                        <motion.div
                            className="relative mx-auto"
                            style={{
                                width: '100%',
                                maxWidth: '900px',
                                height: '500px',
                                transformStyle: 'preserve-3d'
                            }}
                            animate={{
                                rotateY: [0, 2, 0, -2, 0],
                            }}
                            transition={{
                                duration: 8,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            {/* Background Glow */}
                            <div
                                className="absolute inset-0 rounded-3xl"
                                style={{
                                    background: 'radial-gradient(circle at center, rgba(110, 255, 157, 0.2), transparent 70%)',
                                    filter: 'blur(40px)'
                                }}
                            />

                            {/* Main Platform Image - Using real images */}
                            <div className="relative w-full h-full rounded-3xl overflow-hidden glass-card p-6">
                                <img
                                    src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1200&auto=format&fit=crop"
                                    alt="Green Energy Landscape"
                                    className="absolute inset-0 w-full h-full object-cover opacity-30"
                                />

                                {/* Overlay Graphics */}
                                <div className="relative z-10 h-full flex items-center justify-center">
                                    {/* Central Logo/Badge */}
                                    <motion.div
                                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                                        animate={{
                                            scale: [1, 1.05, 1],
                                            rotate: [0, 5, -5, 0]
                                        }}
                                        transition={{
                                            duration: 4,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        <div
                                            className="w-32 h-32 md:w-40 md:h-40 rounded-full flex items-center justify-center"
                                            style={{
                                                background: 'radial-gradient(circle, rgba(110, 255, 157, 0.4), rgba(15, 23, 42, 0.9))',
                                                border: '3px solid var(--neon-green)',
                                                boxShadow: '0 0 60px rgba(110, 255, 157, 0.5), inset 0 0 30px rgba(110, 255, 157, 0.2)'
                                            }}
                                        >
                                            <div className="text-center">
                                                <div className="text-5xl font-bold font-mono" style={{ color: 'var(--neon-green)' }}>8</div>
                                                <div className="text-xs font-mono" style={{ color: 'var(--secondary-text)' }}>GREENOVA</div>
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Floating Energy Icons */}
                                    <motion.div
                                        className="absolute top-10 left-10"
                                        animate={{ y: [0, -15, 0], rotate: [0, 10, 0] }}
                                        transition={{ duration: 3, repeat: Infinity }}
                                    >
                                        <div
                                            className="w-16 h-16 rounded-full flex items-center justify-center"
                                            style={{
                                                background: 'rgba(110, 255, 157, 0.2)',
                                                border: '2px solid var(--neon-green)',
                                                backdropFilter: 'blur(10px)',
                                                boxShadow: '0 0 30px rgba(110, 255, 157, 0.4)'
                                            }}
                                        >
                                            <Sun className="w-8 h-8" style={{ color: 'var(--neon-green)' }} />
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="absolute top-10 right-10"
                                        animate={{ y: [0, -15, 0], rotate: [0, -10, 0] }}
                                        transition={{ duration: 3, delay: 1, repeat: Infinity }}
                                    >
                                        <div
                                            className="w-16 h-16 rounded-full flex items-center justify-center"
                                            style={{
                                                background: 'rgba(14, 165, 233, 0.2)',
                                                border: '2px solid var(--cyber-blue)',
                                                backdropFilter: 'blur(10px)',
                                                boxShadow: '0 0 30px rgba(14, 165, 233, 0.4)'
                                            }}
                                        >
                                            <Wind className="w-8 h-8" style={{ color: 'var(--cyber-blue)' }} />
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="absolute bottom-10 left-1/4"
                                        animate={{ y: [0, -15, 0], rotate: [0, 10, 0] }}
                                        transition={{ duration: 3, delay: 0.5, repeat: Infinity }}
                                    >
                                        <div
                                            className="w-16 h-16 rounded-full flex items-center justify-center"
                                            style={{
                                                background: 'rgba(139, 92, 246, 0.2)',
                                                border: '2px solid var(--electric-purple)',
                                                backdropFilter: 'blur(10px)',
                                                boxShadow: '0 0 30px rgba(139, 92, 246, 0.4)'
                                            }}
                                        >
                                            <Battery className="w-8 h-8" style={{ color: 'var(--electric-purple)' }} />
                                        </div>
                                    </motion.div>

                                    <motion.div
                                        className="absolute bottom-10 right-1/4"
                                        animate={{ y: [0, -15, 0], rotate: [0, -10, 0] }}
                                        transition={{ duration: 3, delay: 1.5, repeat: Infinity }}
                                    >
                                        <div
                                            className="w-16 h-16 rounded-full flex items-center justify-center"
                                            style={{
                                                background: 'rgba(14, 165, 233, 0.2)',
                                                border: '2px solid var(--cyber-blue)',
                                                backdropFilter: 'blur(10px)',
                                                boxShadow: '0 0 30px rgba(14, 165, 233, 0.4)'
                                            }}
                                        >
                                            <Globe className="w-8 h-8" style={{ color: 'var(--cyber-blue)' }} />
                                        </div>
                                    </motion.div>
                                </div>

                                {/* Energy Connection Lines */}
                                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                                    <motion.circle
                                        cx="50%"
                                        cy="50%"
                                        r="150"
                                        fill="none"
                                        stroke="var(--neon-green)"
                                        strokeWidth="1"
                                        opacity="0.3"
                                        strokeDasharray="5,5"
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                                    />
                                    <motion.circle
                                        cx="50%"
                                        cy="50%"
                                        r="200"
                                        fill="none"
                                        stroke="var(--cyber-blue)"
                                        strokeWidth="1"
                                        opacity="0.2"
                                        strokeDasharray="5,5"
                                        animate={{ rotate: -360 }}
                                        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                                    />
                                </svg>
                            </div>
                        </motion.div>

                        {/* Bottom Info Cards */}
                        <motion.div
                            className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 w-full max-w-4xl"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1, duration: 0.8 }}
                        >
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-4">
                                <div className="glass-card p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold" style={{ color: 'var(--neon-green)' }}>25+</div>
                                    <div className="text-xs font-mono" style={{ color: 'var(--secondary-text)' }}>PROJECTS</div>
                                </div>
                                <div className="glass-card p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold" style={{ color: 'var(--cyber-blue)' }}>$2.5M</div>
                                    <div className="text-xs font-mono" style={{ color: 'var(--secondary-text)' }}>INVESTED</div>
                                </div>
                                <div className="glass-card p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold" style={{ color: 'var(--electric-purple)' }}>1.2K+</div>
                                    <div className="text-xs font-mono" style={{ color: 'var(--secondary-text)' }}>INVESTORS</div>
                                </div>
                                <div className="glass-card p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold" style={{ color: 'var(--warning)' }}>75 MW</div>
                                    <div className="text-xs font-mono" style={{ color: 'var(--secondary-text)' }}>ENERGY</div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                    className="flex flex-col sm:flex-row gap-6 justify-center items-center mt-32"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.8 }}
                >
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        {user ? (
                            <Link
                                to="/projects"
                                className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300"
                                style={{
                                    background: 'linear-gradient(135deg, var(--neon-green), var(--eco-green))',
                                    color: 'var(--primary-bg)',
                                    boxShadow: '0 0 30px rgba(110, 255, 157, 0.4)'
                                }}
                            >
                                <Zap className="w-6 h-6" />
                                START INVESTING
                            </Link>
                        ) : (
                            <Link
                                to="/login"
                                className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300"
                                style={{
                                    background: 'linear-gradient(135deg, var(--neon-green), var(--eco-green))',
                                    color: 'var(--primary-bg)',
                                    boxShadow: '0 0 30px rgba(110, 255, 157, 0.4)'
                                }}
                            >
                                <Zap className="w-6 h-6" />
                                START INVESTING
                            </Link>
                        )}
                    </motion.div>

                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Link
                            to="/how-it-works"
                            className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300"
                            style={{
                                background: 'rgba(14, 165, 233, 0.1)',
                                border: '2px solid var(--cyber-blue)',
                                color: 'var(--cyber-blue)',
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            <TrendingUp className="w-6 h-6" />
                            LEARN MORE
                        </Link>
                    </motion.div>
                </motion.div>

                {/* Trust Indicators */}
                <motion.div
                    className="text-center mt-16"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4, duration: 0.8 }}
                >
                    <div className="flex flex-wrap justify-center items-center gap-8">
                        <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5" style={{ color: 'var(--neon-green)' }} />
                            <span className="text-sm font-mono" style={{ color: 'var(--secondary-text)' }}>
                                BLOCKCHAIN SECURED
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Globe className="w-5 h-5" style={{ color: 'var(--cyber-blue)' }} />
                            <span className="text-sm font-mono" style={{ color: 'var(--secondary-text)' }}>
                                GLOBAL IMPACT
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Zap className="w-5 h-5" style={{ color: 'var(--electric-purple)' }} />
                            <span className="text-sm font-mono" style={{ color: 'var(--secondary-text)' }}>
                                REAL-TIME TRACKING
                            </span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}






